import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Switch,
  Divider,
  Button,
  Stack,
  Alert,
  FormControlLabel,
  Chip,
} from "@mui/material";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/authContext";
import { useOptions } from "../../context/optionContext";
import { NotificationEvents, NotificationEventLabels } from "../../constants/notificationEvents";
import { updateUser } from "../../services/userService";

const parseBool = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
  }
  return fallback;
};

const EVENT_DETAILS = {
  [NotificationEvents.USER_FORGOT_PASSWORD]: "Alert when your account password reset is requested.",
  [NotificationEvents.USER_PASSWORD_CHANGED]: "Alert when your account password is changed.",
  [NotificationEvents.USER_ASSIGNED_NEW_LOCATION]: "Alert when your access is updated with new customer/site allocations.",
  [NotificationEvents.USER_REQUESTLIST_PURCHASABLE]: "Alert when request-list parts become purchasable.",
  [NotificationEvents.USER_NEW_DOCUMENT_ADDED]: "Alert when new documents are published for your allowed sites.",
  [NotificationEvents.USER_ORDER_SUCCESS]: "Alert when your order is successfully submitted.",
  [NotificationEvents.USER_PART_ORDER_REMINDER]: "Reminder to complete pending part orders.",
  [NotificationEvents.ADMIN_NEW_USER_REGISTERED]: "Alert when a new user account is registered.",
  [NotificationEvents.ADMIN_NEW_ORDER_PLACED]: "Alert when a new customer order is placed.",
  [NotificationEvents.ADMIN_NOTIFY_CUSTOMER_CARE]: "Alert when a customer-care/request escalation is raised.",
  [NotificationEvents.ADMIN_NEW_CASE_CREATED]: "Alert when a new support case is created.",
  [NotificationEvents.ADMIN_CONTACT_CCR_FORM]: "Alert when a Contact CCR form is submitted.",
  [NotificationEvents.ADMIN_MACHINE_SELL_NOTIFY]: "Alert when a machine-sale notification is submitted.",
};

const PREFERENCE_GROUPS = [
  {
    id: "account_security",
    title: "Account and Security",
    description: "Personal account and login related alerts.",
    events: [
      NotificationEvents.USER_FORGOT_PASSWORD,
      NotificationEvents.USER_PASSWORD_CHANGED,
      NotificationEvents.USER_ASSIGNED_NEW_LOCATION,
    ],
  },
  {
    id: "orders_requests",
    title: "Orders and Request List",
    description: "Order lifecycle and request-list updates.",
    events: [
      NotificationEvents.USER_ORDER_SUCCESS,
      NotificationEvents.USER_PART_ORDER_REMINDER,
      NotificationEvents.USER_REQUESTLIST_PURCHASABLE,
      NotificationEvents.ADMIN_NEW_ORDER_PLACED,
      NotificationEvents.ADMIN_NOTIFY_CUSTOMER_CARE,
    ],
  },
  {
    id: "cases_support",
    title: "Cases and Customer Care",
    description: "Support-case and CCR submission alerts.",
    events: [
      NotificationEvents.ADMIN_NEW_CASE_CREATED,
      NotificationEvents.ADMIN_CONTACT_CCR_FORM,
    ],
  },
  {
    id: "platform_updates",
    title: "Platform and Content",
    description: "Operational and content-level notifications.",
    events: [
      NotificationEvents.USER_NEW_DOCUMENT_ADDED,
      NotificationEvents.ADMIN_MACHINE_SELL_NOTIFY,
    ],
  },
  {
    id: "user_admin",
    title: "User Administration",
    description: "User-management oversight notifications.",
    events: [NotificationEvents.ADMIN_NEW_USER_REGISTERED],
  },
];

const isEventVisible = ({ eventKey, isAdmin, hasPermission, notifyOrderRequest, machineSellNotifyEnabled }) => {
  const adminEvents = new Set([
    NotificationEvents.ADMIN_NEW_USER_REGISTERED,
    NotificationEvents.ADMIN_NEW_ORDER_PLACED,
    NotificationEvents.ADMIN_NOTIFY_CUSTOMER_CARE,
    NotificationEvents.ADMIN_NEW_CASE_CREATED,
    NotificationEvents.ADMIN_CONTACT_CCR_FORM,
    NotificationEvents.ADMIN_MACHINE_SELL_NOTIFY,
  ]);
  if (adminEvents.has(eventKey) && !isAdmin) return false;

  switch (eventKey) {
    case NotificationEvents.ADMIN_NEW_USER_REGISTERED:
      return hasPermission("user_management.can_view_pending_users_for_approval");
    case NotificationEvents.ADMIN_NEW_ORDER_PLACED:
      return notifyOrderRequest && (hasPermission("order_management.view_order_history") || hasPermission("order_management.create_order"));
    case NotificationEvents.ADMIN_NOTIFY_CUSTOMER_CARE:
      return notifyOrderRequest && (hasPermission("requests_management.view_request_history") || hasPermission("requests_management.allow_notify_customer_care"));
    case NotificationEvents.ADMIN_NEW_CASE_CREATED:
      return hasPermission("service_management.view_cases") || hasPermission("service_management.create_cases");
    case NotificationEvents.ADMIN_CONTACT_CCR_FORM:
      return notifyOrderRequest && (hasPermission("requests_management.view_request_history") || hasPermission("requests_management.allow_notify_customer_care"));
    case NotificationEvents.ADMIN_MACHINE_SELL_NOTIFY:
      return machineSellNotifyEnabled && (hasPermission("order_management.view_order_history") || hasPermission("order_management.create_order"));
    case NotificationEvents.USER_REQUESTLIST_PURCHASABLE:
      return hasPermission("requests_management.view_request_history");
    case NotificationEvents.USER_ORDER_SUCCESS:
    case NotificationEvents.USER_PART_ORDER_REMINDER:
      return hasPermission("order_management.view_order_history") || hasPermission("order_management.create_order");
    default:
      return true;
  }
};

export default function NotificationPreferences() {
  const { user, updateUserStatus, hasPermission } = useAuth();
  const { optionKeys } = useOptions();
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState({});
  const [saving, setSaving] = useState(false);

  const isAdmin = String(user?.role?.role_type || "").trim().toLowerCase() === "admin";
  const notifyOrderRequest = parseBool(user?.user_meta?.notify_order_creations_and_requests, false);
  const machineSellNotifyEnabled = parseBool(optionKeys?.enableMachineSellNotifyForm, false);

  const visibleGroups = useMemo(() => {
    const groups = PREFERENCE_GROUPS.map((group) => {
      const visibleEvents = group.events.filter((eventKey) =>
        isEventVisible({ eventKey, isAdmin, hasPermission, notifyOrderRequest, machineSellNotifyEnabled })
      );
      return { ...group, visibleEvents };
    }).filter((group) => group.visibleEvents.length > 0);
    return groups;
  }, [hasPermission, isAdmin, notifyOrderRequest, machineSellNotifyEnabled]);

  const visibleEventKeys = useMemo(
    () => visibleGroups.flatMap((group) => group.visibleEvents),
    [visibleGroups]
  );

  useEffect(() => {
    const existing = user?.user_meta?.notification_preferences;
    let parsed = {};
    try {
      parsed = typeof existing === "string" ? JSON.parse(existing) : existing || {};
    } catch (_err) {
      parsed = {};
    }
    const defaults = {};
    Object.values(NotificationEvents).forEach((eventKey) => {
      defaults[eventKey] = { email: true, inapp: true };
    });
    setPrefs({ ...defaults, ...parsed });
  }, [user]);

  const toggle = (key, channel) => (e) => {
    const checked = e.target.checked;
    setPrefs((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [channel]: checked },
    }));
  };

  const save = async () => {
    try {
      setSaving(true);
      const filteredPrefs = {};
      visibleEventKeys.forEach((eventKey) => {
        filteredPrefs[eventKey] = {
          email: !!prefs?.[eventKey]?.email,
          inapp: !!prefs?.[eventKey]?.inapp,
        };
      });

      const reqBody = {
        userId: user.user_id,
        mode: "profile",
        data: {
          user_first_name: user.user_first_name,
          user_last_name: user.user_last_name,
          user_email: user.user_email,
          user_role: user.user_role,
          user_display_name: user.user_display_name,
          user_phone: user.user_phone,
          user_meta: {
            notification_preferences: JSON.stringify(filteredPrefs),
          },
        },
      };
      const res = await updateUser(reqBody);
      if (!res?.status) throw new Error("Save failed");
      updateUserStatus({
        user_meta: {
          ...(user?.user_meta || {}),
          notification_preferences: filteredPrefs,
        },
      });
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: t("UI_TEXT.NOTIFICATION_PREFERENCES_UPDATED"),
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.message || "Failed to save preferences.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Stack spacing={0.25} sx={{ mb: 1.25 }}>
        <Typography variant="h6">{t("UI_TEXT.NOTIFICATIONS_1")}</Typography>
        <Typography variant="body2" color="text.secondary">
          Choose how you want to receive each notification. Only relevant options for your role and access are shown.
        </Typography>
      </Stack>

      {isAdmin && !notifyOrderRequest && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Order and request escalation admin notifications are hidden because "Receive notifications for order creations and request list" is disabled for this user.
        </Alert>
      )}

      <Stack spacing={2}>
        {visibleGroups.map((group) => (
          <Box
            key={group.id}
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1.1,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 0.3 }}
            >
              <Stack spacing={0.3} justifyContent="center">
                <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {group.title}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.2 }}>
                  {group.description}
                </Typography>
              </Stack>
              <Chip
                size="small"
                label={`${group.visibleEvents.length} options`}
                sx={{ fontSize: 11 }}
              />
            </Stack>
            <Divider sx={{ my: "8px" }} />

            <Grid container spacing={1.25}>
              {group.visibleEvents.map((eventKey) => (
                <Grid item xs={12} md={6} key={eventKey}>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      backgroundColor: "#fff",
                      px: 1.25,
                      py: 0.9,
                      height: "100%",
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Stack spacing={0.35} justifyContent="center">
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                            {NotificationEventLabels[eventKey] || eventKey}
                          </Typography>
                          {String(eventKey || "").startsWith("ADMIN_") && (
                            <Chip
                              size="small"
                              label="Admin only"
                              sx={{ height: 18, fontSize: 10, fontWeight: 500 }}
                            />
                          )}
                        </Stack>
                        <Typography variant="body2" sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.2 }}>
                          {EVENT_DETAILS[eventKey] || "Notification preference"}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={2}>
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Switch
                              size="small"
                              checked={!!prefs?.[eventKey]?.email}
                              onChange={toggle(eventKey, "email")}
                            />
                          }
                          label={<Typography sx={{ fontSize: 12 }}>Email</Typography>}
                        />
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Switch
                              size="small"
                              checked={!!prefs?.[eventKey]?.inapp}
                              onChange={toggle(eventKey, "inapp")}
                            />
                          }
                          label={<Typography sx={{ fontSize: 12 }}>In-app</Typography>}
                        />
                      </Stack>
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Stack>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1 }}>
        <Button variant="outlined" onClick={() => window.location.reload()} disabled={saving}>
          {t("BUTTON.CANCEL")}
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          Save Preferences
        </Button>
      </Box>
    </Box>
  );
}
